import { useCallback, useEffect, useMemo, useRef } from 'react'
import debounce from 'lodash/debounce'
import { usePopover } from 'gu-ui-kit/Popover'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'

export const DRAWER_FOOTER_POPOVER_MIN_WIDTH = 768

export const DRAWER_FOOTER_POPOVER_CLOSE_DELAY_MS = 150

export const DRAWER_FOOTER_MENU_FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

export const DRAWER_FOOTER_POPOVER_BREAKPOINTS = {
  0: {
    variant: 'bottomSheet',
    hasBackdrop: true,
  },
  [DRAWER_FOOTER_POPOVER_MIN_WIDTH]: {
    variant: 'popover',
    hasBackdrop: false,
  },
}

/** collapsed desktop popover → hover; expanded desktop / bottomSheet → click */
export const shouldUseDrawerFooterHoverMode = (isPopoverViewport, collapse) =>
  Boolean(isPopoverViewport && collapse)

export const getDrawerFooterInteractionConfig = ({ isPopoverViewport, collapse }) => {
  const useHoverMode = shouldUseDrawerFooterHoverMode(isPopoverViewport, collapse)

  return {
    useHoverMode,
    isPopoverViewport: Boolean(isPopoverViewport),
    popoverEvents: useHoverMode ? DRAWER_FOOTER_HOVER_POPOVER_EVENTS : {},
    popoverConfig: isPopoverViewport ? getDrawerFooterPopoverConfig(collapse) : {},
  }
}

export const DRAWER_FOOTER_POPOVER_OFFSET = 8

export const DRAWER_FOOTER_EXPANDED_POPOVER_CONFIG = {
  anchorOrigin: { vertical: 'top', horizontal: 'left' },
  transformOrigin: { vertical: DRAWER_FOOTER_POPOVER_OFFSET, horizontal: 'left' },
  stretch: true,
}

export const DRAWER_FOOTER_HOVER_POPOVER_EVENTS = {
  triggerOpenEventName: 'onMouseEnter',
  triggerCloseEventName: 'onMouseLeave',
}

export const getDrawerFooterPopoverConfig = (collapse) => {
  if (collapse) {
    return {
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
      transformOrigin: { vertical: 'top', horizontal: DRAWER_FOOTER_POPOVER_OFFSET },
      stretch: false,
    }
  }

  return DRAWER_FOOTER_EXPANDED_POPOVER_CONFIG
}

export const getDrawerFooterAnchorEl = (
  footerAnchorRef,
  triggerEl,
  collapse,
  { useFooterAnchor = true } = {},
) => {
  if (collapse || !useFooterAnchor) {
    return triggerEl
  }

  return (
    footerAnchorRef?.current ??
    document.querySelector('[data-drawer-footer-anchor]') ??
    triggerEl
  )
}

export const getDrawerFooterMenuFocusables = (container) => {
  if (!container) {
    return []
  }

  return Array.from(container.querySelectorAll(DRAWER_FOOTER_MENU_FOCUSABLE_SELECTOR)).filter(
    (el) => el.getAttribute('aria-hidden') !== 'true',
  )
}

export const focusDrawerFooterMenuItem = (container, index = 0) => {
  const items = getDrawerFooterMenuFocusables(container)
  if (!items.length) {
    return false
  }

  const target = items[index] ?? items[0]
  target.focus()
  return true
}

export const bindDrawerFooterTrigger = (
  bindTrigger,
  popoverState,
  {
    collapse,
    footerAnchorRef,
    useFooterAnchor = true,
    useHoverMode = false,
    onScheduleClose,
    onCancelScheduledClose,
    onTriggerEl,
    onKeyDown,
    onTriggerBlur,
    shouldSuppressOpenOnFocus,
    clearSuppressOpenOnFocus,
  },
) => {
  const bindings = bindTrigger(popoverState)

  const openWithAnchor = (event) => {
    event.stopPropagation()
    if (onTriggerEl) {
      onTriggerEl(event.currentTarget)
    }
    if (onCancelScheduledClose) {
      onCancelScheduledClose()
    }

    const anchorEl = getDrawerFooterAnchorEl(footerAnchorRef, event.currentTarget, collapse, {
      useFooterAnchor,
    })

    if (!anchorEl) {
      return
    }

    popoverState.open({
      id: popoverState.id,
      anchorEl,
    })
  }

  const withTriggerMeta = (handler) => (event) => {
    if (onTriggerEl) {
      onTriggerEl(event.currentTarget)
    }
    if (handler) {
      handler(event)
    }
  }

  if (useHoverMode) {
    const { onMouseEnter: _onMouseEnter, onMouseLeave: _onMouseLeave, ...rest } = bindings

    return {
      ...rest,
      onMouseEnter: openWithAnchor,
      onMouseLeave: (event) => {
        // Keep open while keyboard focus stays on the trigger (mirrors Nav flyout).
        if (event.currentTarget.contains(document.activeElement)) {
          return
        }
        if (onScheduleClose) {
          onScheduleClose()
        }
      },
      onFocus: (event) => {
        if (shouldSuppressOpenOnFocus?.()) {
          clearSuppressOpenOnFocus?.()
          if (onTriggerEl) {
            onTriggerEl(event.currentTarget)
          }
          return
        }
        openWithAnchor(event)
      },
      onBlur: withTriggerMeta(onTriggerBlur),
      onKeyDown: withTriggerMeta(onKeyDown),
    }
  }

  const { onClick: _onClick, ...rest } = bindings

  return {
    ...rest,
    onClick: (event) => {
      if (popoverState.isOpen) {
        event.stopPropagation()
        popoverState.close()
        return
      }

      openWithAnchor(event)
    },
    onBlur: withTriggerMeta(onTriggerBlur),
    onKeyDown: withTriggerMeta(onKeyDown),
  }
}

export const useDrawerFooterMenuInteractions = ({ collapse, footerAnchorRef, id }) => {
  const breakpoints = useBreakpoint()
  const { useHoverMode, isPopoverViewport, popoverEvents, popoverConfig } = useMemo(
    () =>
      getDrawerFooterInteractionConfig({
        isPopoverViewport: breakpoints.fb,
        collapse,
      }),
    [breakpoints.fb, collapse],
  )
  // Arrow nav for desktop popover in both collapsed (side flyout) and expanded (above) modes.
  const useArrowNav = isPopoverViewport
  const { bindTrigger, bindPopover, usePopupState } = usePopover(popoverEvents)
  const popoverState = usePopupState({
    variant: 'popover',
    id,
  })

  const closeRef = useRef(popoverState.close)
  closeRef.current = popoverState.close

  const isOpenRef = useRef(popoverState.isOpen)
  isOpenRef.current = popoverState.isOpen

  const scheduleCloseRef = useRef(null)
  const triggerElRef = useRef(null)
  const contentRef = useRef(null)
  const pendingFocusMenuRef = useRef(false)
  const suppressOpenOnFocusRef = useRef(false)

  useEffect(() => {
    scheduleCloseRef.current = debounce(() => {
      closeRef.current()
    }, DRAWER_FOOTER_POPOVER_CLOSE_DELAY_MS)

    return () => {
      if (scheduleCloseRef.current) {
        scheduleCloseRef.current.cancel()
      }
    }
  }, [])

  const cancelScheduledClose = useCallback(() => {
    if (scheduleCloseRef.current) {
      scheduleCloseRef.current.cancel()
    }
  }, [])

  const scheduleClose = useCallback(() => {
    if (scheduleCloseRef.current) {
      scheduleCloseRef.current()
    }
  }, [])

  const setTriggerEl = useCallback((el) => {
    triggerElRef.current = el
  }, [])

  const shouldSuppressOpenOnFocus = useCallback(() => suppressOpenOnFocusRef.current, [])

  const clearSuppressOpenOnFocus = useCallback(() => {
    suppressOpenOnFocusRef.current = false
  }, [])

  const returnFocusToTrigger = useCallback(() => {
    suppressOpenOnFocusRef.current = true
    triggerElRef.current?.focus()
  }, [])

  const focusEnterMenuItem = useCallback(() => {
    const items = getDrawerFooterMenuFocusables(contentRef.current)
    if (!items.length) {
      return
    }
    // Collapsed: menu to the right → first item. Expanded: menu above → nearest (last) item.
    const target = collapse ? items[0] : items[items.length - 1]
    target.focus()
  }, [collapse])

  useEffect(() => {
    if (!pendingFocusMenuRef.current || !popoverState.isOpen) {
      return undefined
    }

    pendingFocusMenuRef.current = false
    const frame = requestAnimationFrame(() => {
      focusEnterMenuItem()
    })

    return () => cancelAnimationFrame(frame)
  }, [focusEnterMenuItem, popoverState.isOpen])

  const handleContentMouseEnter = useCallback(() => {
    popoverState.lock()
    cancelScheduledClose()
  }, [cancelScheduledClose, popoverState])

  const handleContentMouseLeave = useCallback(() => {
    popoverState.lock()
    scheduleClose()
  }, [popoverState, scheduleClose])

  const handleContentFocus = useCallback(() => {
    cancelScheduledClose()
  }, [cancelScheduledClose])

  const isRelatedTargetOnTrigger = useCallback((relatedTarget) => {
    if (!relatedTarget || !triggerElRef.current) {
      return false
    }
    return (
      relatedTarget === triggerElRef.current || triggerElRef.current.contains(relatedTarget)
    )
  }, [])

  const isRelatedTargetInMenuOrNestedOverlay = useCallback((relatedTarget) => {
    if (!relatedTarget) {
      return false
    }
    if (contentRef.current?.contains(relatedTarget)) {
      return true
    }
    return Boolean(relatedTarget.closest?.('#react-portal-popover-container'))
  }, [])

  const closeIfFocusLeft = useCallback(() => {
    const active = document.activeElement
    if (triggerElRef.current?.contains(active) || contentRef.current?.contains(active)) {
      return
    }
    // Nested Select/Popover may keep focus in the shared popover portal.
    if (active?.closest?.('#react-portal-popover-container')) {
      cancelScheduledClose()
      return
    }
    cancelScheduledClose()
    closeRef.current()
  }, [cancelScheduledClose])

  const handleTriggerBlur = useCallback(
    (event) => {
      if (event.currentTarget.contains(event.relatedTarget)) {
        return
      }
      if (isRelatedTargetInMenuOrNestedOverlay(event.relatedTarget)) {
        cancelScheduledClose()
        return
      }
      // relatedTarget can be null while Tab moves focus into a portaled menu.
      requestAnimationFrame(closeIfFocusLeft)
    },
    [cancelScheduledClose, closeIfFocusLeft, isRelatedTargetInMenuOrNestedOverlay],
  )

  const handleContentBlur = useCallback(
    (event) => {
      if (event.currentTarget.contains(event.relatedTarget)) {
        return
      }
      if (isRelatedTargetOnTrigger(event.relatedTarget)) {
        cancelScheduledClose()
        return
      }
      requestAnimationFrame(closeIfFocusLeft)
    },
    [cancelScheduledClose, closeIfFocusLeft, isRelatedTargetOnTrigger],
  )

  const handleTriggerKeyDown = useCallback(
    (event) => {
      if (!useArrowNav) {
        return
      }

      const enterKey = collapse ? 'ArrowRight' : 'ArrowUp'
      const leaveKey = collapse ? 'ArrowLeft' : 'ArrowDown'

      if (event.key === enterKey) {
        event.preventDefault()
        cancelScheduledClose()

        if (!isOpenRef.current) {
          pendingFocusMenuRef.current = true
          const anchorEl = getDrawerFooterAnchorEl(
            footerAnchorRef,
            event.currentTarget,
            collapse,
          )
          popoverState.open({
            id: popoverState.id,
            anchorEl,
          })
          return
        }

        focusEnterMenuItem()
        return
      }

      if (event.key === leaveKey && isOpenRef.current) {
        event.preventDefault()
        closeRef.current()
      }
    },
    [
      cancelScheduledClose,
      collapse,
      focusEnterMenuItem,
      footerAnchorRef,
      popoverState,
      useArrowNav,
    ],
  )

  const handleContentKeyDown = useCallback(
    (event) => {
      if (!useArrowNav) {
        return
      }

      const items = getDrawerFooterMenuFocusables(event.currentTarget)
      const index = items.indexOf(document.activeElement)

      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        closeRef.current()
        returnFocusToTrigger()
        return
      }

      // DropdownItem defaults to a non-interactive div — activate like a button.
      if (event.key === 'Enter' || event.key === ' ') {
        const active = document.activeElement
        if (!items.includes(active)) {
          return
        }
        if (active.tagName === 'BUTTON' || active.tagName === 'A') {
          return
        }
        event.preventDefault()
        active.click()
        return
      }

      if (collapse) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          event.stopPropagation()
          cancelScheduledClose()
          triggerElRef.current?.focus()
          return
        }

        if (!items.length || index < 0) {
          return
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault()
          items[(index + 1) % items.length]?.focus()
          return
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault()
          items[(index - 1 + items.length) % items.length]?.focus()
        }
        return
      }

      // Expanded: menu above trigger — Up/Down follow vertical layout.
      if (!items.length || index < 0) {
        return
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        if (index >= items.length - 1) {
          cancelScheduledClose()
          triggerElRef.current?.focus()
          return
        }
        items[index + 1]?.focus()
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (index <= 0) {
          cancelScheduledClose()
          triggerElRef.current?.focus()
          return
        }
        items[index - 1]?.focus()
      }
    },
    [cancelScheduledClose, collapse, returnFocusToTrigger, useArrowNav],
  )

  const triggerBindings = useMemo(
    () =>
      isPopoverViewport
        ? bindDrawerFooterTrigger(bindTrigger, popoverState, {
            collapse,
            footerAnchorRef,
            useHoverMode,
            onScheduleClose: scheduleClose,
            onCancelScheduledClose: cancelScheduledClose,
            onTriggerEl: setTriggerEl,
            onKeyDown: handleTriggerKeyDown,
            onTriggerBlur: handleTriggerBlur,
            shouldSuppressOpenOnFocus,
            clearSuppressOpenOnFocus,
          })
        : bindTrigger(popoverState),
    [
      bindTrigger,
      cancelScheduledClose,
      clearSuppressOpenOnFocus,
      collapse,
      footerAnchorRef,
      handleTriggerBlur,
      handleTriggerKeyDown,
      isPopoverViewport,
      popoverState,
      scheduleClose,
      setTriggerEl,
      shouldSuppressOpenOnFocus,
      useHoverMode,
    ],
  )

  const popoverAnchorEl = useMemo(() => {
    if (!isPopoverViewport || collapse) {
      return popoverState.anchorEl
    }

    return (
      footerAnchorRef?.current ??
      document.querySelector('[data-drawer-footer-anchor]') ??
      popoverState.anchorEl
    )
  }, [collapse, footerAnchorRef, isPopoverViewport, popoverState.anchorEl, popoverState.isOpen])

  const popoverBindings = useMemo(
    () => ({
      ...bindPopover(popoverState),
      ...(isPopoverViewport ? { anchorEl: popoverAnchorEl } : {}),
      ...popoverConfig,
      breakpoints: DRAWER_FOOTER_POPOVER_BREAKPOINTS,
    }),
    [bindPopover, isPopoverViewport, popoverAnchorEl, popoverConfig, popoverState],
  )

  const contentHoverHandlers = useMemo(() => {
    if (!useArrowNav) {
      return {}
    }

    return {
      ref: contentRef,
      onKeyDown: handleContentKeyDown,
      onFocus: handleContentFocus,
      onBlur: handleContentBlur,
      ...(useHoverMode
        ? {
            onMouseEnter: handleContentMouseEnter,
            onMouseLeave: handleContentMouseLeave,
          }
        : {}),
    }
  }, [
    handleContentBlur,
    handleContentFocus,
    handleContentKeyDown,
    handleContentMouseEnter,
    handleContentMouseLeave,
    useArrowNav,
    useHoverMode,
  ])

  return {
    useHoverMode,
    isPopoverViewport,
    popoverState,
    triggerBindings,
    popoverBindings,
    contentHoverHandlers,
  }
}
